import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Rocket } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Badge "Phase Pilote — X/10 places restantes"
 * - Affiche un compteur live de places gratuites quand le mode pilote est actif
 * - Affiche "Phase pilote complète" quand 0 place restante
 * - N'affiche rien quand pilot_mode est désactivé ou terminé
 *
 * Utilisé sur /login et /register pour créer un effet d'urgence (recrutement LinkedIn).
 */
export function PilotSlotsBadge({ className = '' }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/pilot-mode/public`)
      .then(res => { if (mounted) setInfo(res.data); })
      .catch(() => { if (mounted) setInfo(null); });
    return () => { mounted = false; };
  }, []);

  if (!info || !info.enabled || info.ended) return null;

  const full = info.places_remaining <= 0;
  const quota = info.quota || 10;
  const taken = quota - info.places_remaining;
  const percent = Math.min(100, Math.round((taken / quota) * 100));

  return (
    <div
      data-testid="pilot-slots-badge"
      className={`relative overflow-hidden rounded-2xl border-2 ${full ? 'border-gray-300 bg-gray-50' : 'border-[#FF6B00] bg-gradient-to-br from-[#FF6B00]/10 via-white to-[#0055FF]/5'} p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${full ? 'bg-gray-400' : 'bg-gradient-to-br from-[#FF6B00] to-[#0055FF]'} flex items-center justify-center text-white shadow-sm`}>
          <Rocket size={20} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-sm font-bold text-[#111827] leading-tight">
              {full ? 'Phase pilote complète' : 'Phase Pilote · Inscription gratuite'}
            </p>
            {!full && (
              <span data-testid="pilot-slots-counter" className="text-xs font-mono font-bold text-[#FF6B00] bg-white border border-[#FF6B00]/30 px-2 py-0.5 rounded-full">
                {info.places_remaining}/{quota} places
              </span>
            )}
          </div>
          <p className="text-xs text-[#4B5563] mt-1 leading-snug">
            {full
              ? 'Les 10 places gratuites ont été attribuées. Inscrivez-vous pour rejoindre la liste d\u2019attente.'
              : 'Rejoignez les 10 premiers professionnels OPC/CSPS qui testent CHANTIX gratuitement, sans engagement.'}
          </p>

          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${full ? 'bg-gray-400' : 'bg-gradient-to-r from-[#FF6B00] to-[#0055FF]'} transition-all duration-500`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Pulse halo for urgency (only when 3 or less remaining) */}
      {!full && info.places_remaining <= 3 && (
        <span className="absolute top-3 right-3 flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6B00] opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF6B00]" />
        </span>
      )}
    </div>
  );
}
